// src/services/registro.js
// ─────────────────────────────────────────────────────────────────────────────
// P0-REG (23 sep 2026) — Registro con código ACES
//
// Estructura canónica de cn_codigos:
//   - ID del documento = el código (ej: ACESCN-EVAL-7SNBX5)   ← OBLIGATORIO
//   - campo 'tipo': 'eval' | 'city' | 'ciudad' | 'prov' | 'org' | 'admin' | 'master'
//   - campo 'usadoPor': null si disponible, uid si usado
//
// Cambios P0-REG:
//   1. Se ELIMINA la búsqueda alternativa por query where('codigo'==X): las reglas
//      (A-02) deniegan `list` sin sesión, así que esa rama solo producía el error
//      "codigo_permisos" (mensaje técnico que veían los evaluadores).
//   2. Normalización tolerante del código: quita espacios internos, guiones
//      tipográficos y mayúsculas (usuarios mayores copian con espacios).
//   3. Mapeo de rol alineado con iOS/Android: prov→provincia, org→org,
//      city/ciudad→ciudad. admin/master NO se pueden auto-registrar (las reglas
//      lo impiden: isEscaladaMaster) → error explícito 'codigo_admin'.
//   4. 'rol' legado (códigos generados por la web antigua sin 'tipo') aceptado.
// ─────────────────────────────────────────────────────────────────────────────
import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/** Normaliza lo que teclea/pega el usuario: "acescn - eval - 7snbx5 " → "ACESCN-EVAL-7SNBX5" */
export function normalizarCodigo(codigo) {
  return String(codigo || '')
    .toUpperCase()
    .replace(/[‐-―−]/g, '-') // guiones tipográficos → '-'
    .replace(/\s+/g, '')                    // espacios en cualquier posición
    .trim();
}

// Validar código ACES (lectura por docId exacto: única operación permitida sin sesión)
export async function validarCodigo(codigo) {
  const codigoUp = normalizarCodigo(codigo);
  if (!codigoUp) throw new Error('codigo_invalido');

  let snap;
  try {
    snap = await getDoc(doc(db, 'cn_codigos', codigoUp));
  } catch (e) {
    console.error('[registro] Error leyendo código:', e.code, e.message);
    if (e.code === 'unavailable' || e.code === 'network-request-failed') throw new Error('error_red');
    throw new Error('codigo_invalido');
  }

  if (!snap.exists()) throw new Error('codigo_invalido');

  const data = snap.data();
  if ((data.usadoPor !== null && data.usadoPor !== undefined && data.usadoPor !== '') || data.usado === true) {
    throw new Error('codigo_usado');
  }
  if (data.expiraEn && typeof data.expiraEn.toDate === 'function' && data.expiraEn.toDate() < new Date()) {
    throw new Error('codigo_expirado');
  }

  const rol = mapTipoToRol(data.tipo || data.rol || inferirTipo(codigoUp));
  if (rol === null) throw new Error('codigo_admin');

  return { valido: true, rol, codigo: codigoUp, codigoDoc: snap, candidaturaId: data.candidaturaId || null };
}

// Mapear 'tipo' → roles.cities (idéntico en iOS / Android / Web)
// Devuelve null para admin/master: no se permite auto-registro con esos roles.
export function mapTipoToRol(tipo) {
  const t = String(tipo || '').toLowerCase();
  switch (t) {
    case 'eval':
    case 'evaluador':  return 'eval';
    case 'prov':
    case 'provincia':  return 'provincia';
    case 'org':
    case 'organizacion': return 'org';
    case 'city':
    case 'ciudad':     return 'ciudad';
    case 'admin':
    case 'master':     return null;
    default:           return 'ciudad';
  }
}

function inferirTipo(codigoUp) {
  if (codigoUp.includes('-EVAL-'))   return 'eval';
  if (codigoUp.includes('-PROV-'))   return 'prov';
  if (codigoUp.includes('-ORG-'))    return 'org';
  if (codigoUp.includes('-MASTER-')) return 'master';
  if (codigoUp.includes('-ADMIN-'))  return 'admin';
  return 'city';
}

// Campos que el registro escribe en el código (deben estar en el allowlist de
// firestore.rules → match /cn_codigos → FIX-8: usado, usadoPor, usadoAt, usadoEn).
export function camposCodigoUsado(uid) {
  return {
    usadoPor: uid,
    usado: true,
    usadoAt: serverTimestamp(),
    usadoEn: serverTimestamp(),
  };
}
